import styled from 'styled-components';
import { size } from 'polished';
import { motion } from 'framer-motion';
import { AbstractButton } from '../../atoms/AbstractButton/AbstractButton';
import { MediaQuery } from '../../../styles/mediaQuery';
import { respondTo } from '../../../styles/helpers/respondTo';
import { pseudo } from '../../../styles/helpers/pseudo';
import { hover } from '../../../styles/helpers/hover';
import { Ease } from '../../../styles/easing';
import { FontWeight, TransitionDuration } from '../../../styles/variables';
import { Paragraph } from '../../atoms/Paragraph/Paragraph';
import { zIndex } from '../../../styles/zIndex';
import { typeStyle } from '../../../styles/typeStyle';

export const StyledInformationWrapper = styled(motion.div)<{
  $isFocussed: boolean;
  $isOpen: boolean;
  $isTablet: boolean;
  $hasMessage: boolean;
}>`
  position: absolute;
  overflow: hidden;
  background-color: ${({ theme }) => theme.color.white};
  border-radius: ${({ theme }) => theme.defaultCssProps.borderRadius.small};
  box-shadow: 0 0.25rem 1.25rem rgba(48, 76, 178, 0.2);
  z-index: ${zIndex('map-overlay')};
  top: 1.25rem;
  left: 1.25rem;
  width: auto;
  height: 2.5rem;
  ${({ $isOpen, $isTablet }) =>
    !$isOpen &&
    $isTablet &&
    `
    top: 1.25rem;
    left: 1.25rem;
    width: auto;
    height: 2.5rem;

      ${hover(`
      transform: scale(1.03);
      box-shadow: 0 0.25rem 1.25rem rgba(48, 76, 178, 0.2);

  `)}
  `}
  ${({ $isOpen, $isTablet }) =>
    $isOpen &&
    $isTablet &&
    `
    top: 0.625rem;
    left: 0.625rem;
    width: 16.25rem;
    height: auto;
    padding: 0.625rem;
  `}
  ${({ $isOpen, $isTablet }) =>
    $isOpen &&
    !$isTablet &&
    `
    top: 0rem;
    left: 0rem;
    width: 100vw;
    height: auto;
    padding: 1.25rem;
  `}
  ${({ $hasMessage, $isOpen }) =>
    !$hasMessage &&
    !$isOpen &&
    `
    margin-right: 0.625rem;
    width: 2.5rem;
    `}
  ${({ $isFocussed, theme }) =>
    $isFocussed &&
    `
    border: 1px solid ${theme.color.blue};
    box-shadow: 0px 0px 4px ${theme.color.blue};
  `}
`;

export const StyledInformationButton = styled(motion.button)<{
  $isOpen: boolean;
  $hasMessage: boolean;
}>`
  ${size('2.5rem', '100%')};
  ${typeStyle.normal};
  font-weight: ${FontWeight.Bold};
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding-left: 2.5rem;
  color: ${({ theme }) => theme.color.white};
  background-color: ${({ theme }) => theme.color.lightBlack};
  ${({ $hasMessage }) =>
    $hasMessage &&
    `
    margin-right: 0.625rem;
    `}
  &::before {
    ${pseudo()};
    ${size('1.625rem')};
    top: 0.4375rem;
    left: 0.4375rem;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.color.white};
    transition: ${TransitionDuration.Slow} ${Ease.EaseOutBack};
    ${({ $isOpen, theme }) =>
      $isOpen &&
      `
      background-color: ${theme.color.lightBlack};
      transform: background-color ${TransitionDuration.Slow};
    `}
  }
  svg {
    ${size('0.875rem')};
    position: absolute;
    left: 0.8125rem;
    transition: ${TransitionDuration.Slow} ${Ease.EaseOutBack};
    color: ${({ theme }) => theme.color.lightBlack};
    ${({ $isOpen, theme }) =>
      $isOpen &&
      `
      color: ${theme.color.white};
      transform: color ${TransitionDuration.Slow};
    `}
  }

  ${({ $isOpen, theme }) =>
    $isOpen &&
    `
    color: ${theme.color.lightBlack};
    background-color: ${theme.color.white};
    transform: background-color color ${TransitionDuration.Slow};
  `}
`;

export const StyledInformationLegend = styled.span`
  cursor: default;
  display: flex;
  margin-left: 3.125rem;
  align-items: center;
  justify-content: center;
  position: absolute;
  transform: translateY(-175%);
  color: ${({ theme }) => theme.color.darkerGrey};
`;

export const StyledCloseButton = styled(AbstractButton)`
  ${size('2.1875rem')};
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 1.375rem;
  left: calc(100vw - 0.625rem);
  transform: translateX(-100%);
  color: ${({ theme }) => theme.color.blue};
  cursor: pointer;

  svg {
    ${size('0.875rem')};
  }

  &:focus {
    transform: translateX(-100%) scale(1.08);
    border: 1px solid ${({ theme }) => theme.color.blue};
    box-shadow: 0 0 4px ${({ theme }) => theme.color.blue};
  }

  @media ${respondTo(MediaQuery.Medium)} {
    top: 0.875rem;
    left: calc(16.25rem - 0.625rem);
  }
`;

export const StyledInformationContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 0.4375rem;
  pointer-events: none;
`;

export const StyledInformationBlock = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 1.375rem;
  color: ${({ theme }) => theme.color.blue};

  svg {
    ${size('1.75rem')};
    position: relative;
    border: 4px solid ${({ theme }) => theme.color.white};
  }
`;

export const StyledIcon = styled.img`
  ${size('1.75rem')};
  position: relative;
  border: 4px solid ${({ theme }) => theme.color.white};
`;

export const StyledInformationDescription = styled(Paragraph)`
  color: ${({ theme }) => theme.color.darkerGrey};
  margin-bottom: 0.4375rem;
`;

export const StyledFlightInformation = styled.span<{ $connected?: boolean }>`
  ${size('2px', '8.25rem')};
  position: absolute;
  bottom: 0.8125rem;
  background: ${({ theme }) => theme.color.blue};

  &::before,
  &::after {
    ${pseudo()};
    ${size('0.5rem')};
    top: 0.0625rem;
    border-radius: 50%;
    background: ${({ theme }) => theme.color.blue};
  }

  &::before {
    left: -0.125rem;
    transform: translate(-100%, -50%);
  }

  &::after {
    right: -0.125rem;
    transform: translate(100%, -50%);
  }

  ${({ $connected, theme }) =>
    $connected &&
    `
    background: transparent;
    background-image: linear-gradient(to right, ${theme.color.blue} 60%, rgba(255,255,255,0) 0%);
    background-position: right;
    background-size: 12px 2px;
    background-repeat: repeat-x;
  `}
`;
